import React from 'react';
import { ArrowRight, Check, Globe2, History, Star, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface HeroSectionProps {
  onQuoteClick: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onQuoteClick }) => {
  const { t } = useTranslation();

  const proofPoints = [
    { label: t('hero.proof.bio', 'Certification Bio'), icon: Star },
    { label: t('hero.proof.export', 'Export Mondial'), icon: Globe2 },
    { label: t('hero.proof.partners', '100+ Fermiers Partenaires'), icon: Users },
  ];

  return (
    <section id="hero" className="relative flex min-h-[720px] items-center overflow-hidden px-4 pb-12 pt-28 sm:min-h-screen lg:pt-24">
      <div className="absolute inset-0 bg-gradient-to-r from-cream-50/90 via-cream-50/70 to-transparent" aria-hidden="true" />

      <div className="relative mx-auto grid w-full max-w-6xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="z-10 text-center lg:text-left"
        >
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-forest-200/25 bg-forest-200/10 px-5 py-2 text-sm font-semibold tracking-wide text-forest-200 backdrop-blur-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-forest-200" />
            {t('hero.eyebrow', 'Excellence since 2004')}
          </div>

          <h1 className="mx-auto max-w-2xl whitespace-pre-line font-display text-4xl font-bold leading-[1.02] text-coffee-200 sm:text-5xl lg:mx-0 lg:text-6xl">
            {t('hero.title', "L'Excellence Agricole\nde Madagascar")}
          </h1>

          <p className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-coffee-100 sm:text-lg lg:mx-0 lg:text-xl">
            {t('hero.subtitle', "Une passion transmise de génération en génération pour cultiver l'exception. Nos gousses de vanille Bourbon, notre café Robusta et nos épices premium incarnent l'art ancestral malgache au service de l'excellence mondiale.")}
          </p>

          <div className="mt-9 flex flex-col items-center gap-3 sm:flex-row sm:justify-center lg:justify-start">
            <button
              type="button"
              onClick={onQuoteClick}
              className="group inline-flex min-h-11 items-center justify-center gap-3 rounded-full bg-forest-200 px-5 py-2 text-sm font-semibold text-cream-50 shadow-lg shadow-forest-200/20 transition-all hover:-translate-y-0.5 hover:bg-forest-100 hover:shadow-glow"
            >
              <span>{t('hero.cta', 'Demander un Devis')}</span>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-cream-50/15 transition-transform group-hover:translate-x-0.5">
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </span>
            </button>
            <button
              type="button"
              onClick={() => document.getElementById('story')?.scrollIntoView({ behavior: 'smooth' })}
              className="group inline-flex min-h-11 items-center justify-center gap-3 rounded-full border-2 border-coffee-200/15 bg-cream-50/25 px-5 py-2 text-sm font-semibold text-coffee-200 transition-all hover:border-forest-200/40 hover:bg-cream-50/70"
            >
              <span>{t('hero.discover', 'Notre histoire')}</span>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-coffee-200/5 transition-transform group-hover:rotate-[-8deg]">
                <History className="h-4 w-4" aria-hidden="true" />
              </span>
            </button>
          </div>

          <div className="mx-auto mt-10 grid w-full max-w-xl grid-cols-1 gap-2.5 sm:grid-cols-3 sm:gap-3 lg:mx-0" aria-label="Nos engagements">
            {proofPoints.map(({ label, icon: Icon }) => (
              <div key={label} className="flex min-h-[58px] items-center gap-2 rounded-2xl border border-white/70 bg-white/65 px-3 py-2.5 text-left text-xs font-semibold text-coffee-100 shadow-soft backdrop-blur-md sm:justify-center sm:px-2 sm:text-center">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-forest-200/10 text-forest-200">
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                <span className="leading-tight">{label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.12, ease: 'easeOut' }}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <div className="relative aspect-[4/5] overflow-visible lg:aspect-auto lg:h-[clamp(480px,68vh,660px)]">
            <div className="h-full w-full overflow-hidden rounded-[2rem] shadow-card">
              <img src="/assets/vanilla_GradeA_TK.jpeg" alt="Vanille Bourbon de Madagascar" className="h-full w-full object-cover" />
              <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-t from-coffee-200/25 via-transparent to-transparent" />
            </div>

            <div className="absolute -right-2 -top-5 flex h-28 w-28 rotate-[-8deg] items-center justify-center rounded-full bg-forest-200 p-4 text-center text-xs font-semibold leading-tight text-cream-50 shadow-glow-lg sm:-right-7 sm:-top-5 sm:h-32 sm:w-32 sm:text-sm">
              <span>{t('hero.badge.line1', '100%')}<br />{t('hero.badge.line2', 'Pur & Naturel')}</span>
            </div>

            <div className="absolute -bottom-6 left-4 right-4 flex items-center gap-3 rounded-2xl border border-cream-200/60 bg-cream-50 p-4 shadow-card sm:left-8 sm:right-auto">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-forest-200 text-cream-50">
                <Check className="h-5 w-5" aria-hidden="true" />
              </div>
              <div>
                <div className="text-sm font-semibold text-coffee-200">{t('hero.badge.years', "20+ Années d'Excellence")}</div>
                <div className="text-xs text-coffee-100">{t('hero.badge.since', 'Depuis 2004')}</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
